import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ContributionLimit, ContributionScheme, LifePlanOverview } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { lifePlanApi, reportsApi } from '../lib/api'
import {
  buildLifePlan,
  buildLifePlanMonths,
  bucketLabel,
  type LifePlanSeries,
  type LifePlanTable,
} from '../lib/life-plan'
import {
  btnAccentSm,
  btnGhost,
  btnIconAccent,
  btnNav,
  btnPrimary,
  btnSecondarySm,
  modalTitle,
  pageSubtitle,
  pageTitle,
  segGroup,
  segItem,
} from '../lib/ui'

/**
 * 収支と資産形成をひとつの表にまとめた画面。
 *
 * 列の単位は「月」（選んだ年の 1〜12 月）と「年」（開始年〜終了年）の 2 つだけ。
 * 行は上から
 *
 *   メンバーの年齢 → 収入 → 支出 → 収支 → 貯蓄率 → 資産の積み上げ → 振替 → 投資
 *
 * の順に並ぶ。「貯蓄率」の下がライフプランの区画で、収支で手元に残った分が
 * どこまで積み上がるのかを続けて読めるようにしている。
 */

/** 制度名の翻訳キー */
const SCHEME_LABEL_KEY = {
  nisa: 'lp_scheme_nisa',
  nisa_tsumitate: 'lp_scheme_nisa_tsumitate',
  nisa_growth: 'lp_scheme_nisa_growth',
  kodomo_nisa: 'lp_scheme_kodomo_nisa',
  nisa_old_tsumitate: 'lp_scheme_nisa_old_tsumitate',
  nisa_old_general: 'lp_scheme_nisa_old_general',
  junior_nisa: 'lp_scheme_junior_nisa',
  ideco: 'lp_scheme_ideco',
  kyosai: 'lp_scheme_kyosai',
  unknown: 'lp_scheme_unknown',
} as const satisfies Record<ContributionScheme, string>

/** 列の単位。日を単位にした列は持たない（1 か月ぶんの明細は取引一覧で見る） */
type ViewUnit = 'month' | 'year'

type SectionType = 'income' | 'expense' | 'transfer' | 'investment'

interface MatrixCategory {
  categoryId: string
  categoryName: string
  categoryIcon: string | null
  categoryColor: string | null
  /** 列順の金額。月単位なら 12 要素、年単位なら年の数ぶん */
  months: number[]
  total: number
}

interface MatrixSection {
  type: SectionType
  total: number
  months: number[]
  categories: MatrixCategory[]
}

/** 列幅（px）: 項目 / 合計 / 各列。項目は枠名に累計・期間の印が付くぶん広めにとる */
const ITEM_WIDTH = 296
const TOTAL_WIDTH = 122
/** 億が付くと +123億4567.9万 の長さになるので、折り返さずに収まる幅を確保する */
const COLUMN_WIDTH = 104
/** ヘッダー行の高さ（px）— 縦スクロール時の sticky 位置に使う */
const HEADER_HEIGHT = 34

/** 濃淡表示の色（収入=緑 / 支出=赤 / 振替=藍 / 投資=紫） */
const HEAT_RGB: Record<SectionType, string> = {
  income: '34, 197, 94',
  expense: '239, 68, 68',
  transfer: '99, 102, 241',
  investment: '139, 92, 246',
}

const AMOUNT_TONE: Record<SectionType, string> = {
  income: 'text-green-600 dark:text-green-400',
  expense: 'text-red-600 dark:text-red-400',
  transfer: 'text-indigo-600 dark:text-indigo-400',
  investment: 'text-violet-600 dark:text-violet-400',
}

/** 表の右端の既定。子が老後に入るまで見通せる長さ */
const DEFAULT_END_YEAR = 2084
const END_YEAR_KEY = 'zeni_life_plan_end_year'
const START_YEAR_KEY = 'zeni_life_plan_start_year'
const UNIT_KEY = 'zeni_life_plan_unit'
/** これ以上伸ばしても読めないので入力の上限にする */
const MAX_END_YEAR = 2200

const pad2 = (n: number) => String(n).padStart(2, '0')
/** 月末は月ごとに変わるので、翌月 0 日＝当月末日として求める */
const lastDayOf = (year: number, month: number) => new Date(year, month, 0).getDate()

function loadYear(key: string, min: number, max: number, fallback: number): number {
  const stored = Number(localStorage.getItem(key))
  if (!Number.isInteger(stored) || stored < min || stored > max) return fallback
  return stored
}

function loadUnit(): ViewUnit {
  return localStorage.getItem(UNIT_KEY) === 'month' ? 'month' : 'year'
}

/** 支出・振替・投資はマイナス表示にして、収入との差が一目で分かるようにする */
function signed(type: SectionType, amount: number): number {
  return type === 'income' ? amount : -amount
}

/** 貯蓄率 = 収支 / 収入。収入が無い期間は算出できないため null を返す */
function savingsRate(netAmount: number, incomeAmount: number): number | null {
  return incomeAmount > 0 ? (netAmount / incomeAmount) * 100 : null
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${rate.toFixed(1)}%`
}

/** 期間リターン（％）。Modified Dietz 法。合計列に出す期間まるごとの利回りに使う */
function periodReturnPercent(
  openingValue: number | null,
  closingValue: number | null,
  contribution: number,
): number | null {
  if (openingValue === null || closingValue === null) return null
  const base = openingValue + contribution / 2
  if (base <= 0) return null
  return Math.round(((closingValue - openingValue - contribution) / base) * 10000) / 100
}

const emptySection = (type: SectionType, columnCount: number): MatrixSection => ({
  type,
  total: 0,
  months: Array.from({ length: columnCount }, () => 0),
  categories: [],
})

/**
 * 期間の金額を期首からの累積に置き換えたセクション。投資枠は「その期間でその枠を
 * どこまで使ったか」を積み上げで見たいので、列ごとの額ではなく走行合計を出せるようにする。
 * 最終列の累積は期間合計と一致するので、合計の列はそのままでよい。
 */
function toCumulative(section: MatrixSection): MatrixSection {
  const running = (months: number[]) => {
    let sum = 0
    return months.map((v) => (sum += v))
  }
  return {
    ...section,
    months: running(section.months),
    categories: section.categories.map((cat) => ({ ...cat, months: running(cat.months) })),
  }
}

/** 表示単位の設定を織り込んだ、この画面で使うフォーマッタ */
function useGridFormat() {
  const { formatJPY, formatSignedJPY, formatPlain } = useAmountFormat()
  /** 期間セル用: 符号付きの素の数値（¥ なし） */
  const formatSigned = (amount: number) => {
    if (amount === 0) return '0'
    return `${amount > 0 ? '' : '-'}${formatPlain(Math.round(Math.abs(amount)))}`
  }
  /**
   * 増減として読ませるセル用: プラスにも符号を添える（¥ なし）。
   * 収支は「その期間でいくら増えたか」なので、評価損益と同じ +/− の書き方にそろえる。
   */
  const formatDelta = (amount: number) => {
    const rounded = Math.round(amount)
    if (rounded === 0) return '0'
    return `${rounded > 0 ? '+' : '-'}${formatPlain(Math.abs(rounded))}`
  }
  /** 増減として読ませる合計セル用（¥ あり） */
  const formatDeltaJPY = (amount: number) => {
    const rounded = Math.round(amount)
    if (rounded === 0) return formatJPY(0)
    return `${rounded > 0 ? '+' : '-'}${formatJPY(Math.abs(rounded))}`
  }
  return { formatJPY, formatSignedJPY, formatSigned, formatDelta, formatDeltaJPY, formatPlain }
}

/** 増減の色。増えたら評価損益と同じ緑、減ったら赤。0 は行の既定色のまま */
function deltaTone(amount: number): string | undefined {
  const rounded = Math.round(amount)
  if (rounded === 0) return undefined
  return rounded > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
}

/** セルが表す条件で絞り込んだ取引一覧への URL を作る関数の型 */
type HrefFor = (opts: { types: string; column?: number; categoryId?: string }) => string

/** セルの中身を取引一覧へのリンクにする。href が無いセルはただのテキストのまま */
function CellLink({ href, title, children }: { href?: string; title?: string; children: ReactNode }) {
  if (!href) return <>{children}</>
  return (
    <Link
      to={href}
      title={title}
      className="block rounded hover:underline underline-offset-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400"
    >
      {children}
    </Link>
  )
}

interface GridCell {
  text: string
  /** 文字色。省くと行の既定色 */
  tone?: string
  /** 取引一覧への絞り込みリンク */
  href?: string
  /** 濃淡表示の背景色 */
  heat?: string
}

/** 行の見た目とセルの並びを、収支の行とライフプランの行で共通にするための入れ物 */
interface GridRowProps {
  /** 列の単位。期間の印（累計 / 月間・年間）の読み方が変わる */
  unit: ViewUnit
  label: string
  testId: string
  cells: GridCell[]
  /** 合計列（月単位のときだけ出す）。省くと空欄 */
  totalCell?: GridCell
  showTotal: boolean
  currentColumn: number
  /** 5 年ごとの節目（年単位のときだけ）。薄く塗って年を数えやすくする */
  isMilestone: (column: number) => boolean
  /** strong: 金融資産残高 / total: 合計・枠の見出し / なし: 通常行 */
  emphasis?: 'strong' | 'total'
  labelExtra?: ReactNode
  /** ホバーで出す補足（枠の見出しでは制度と拠出枠） */
  titleText?: string
  /** セルが何を表す数字か。cumulative: 期末までの積み上げ / period: その期間だけの額 */
  period?: 'cumulative' | 'period'
  linkTitle?: string
}

export default function LifePlan() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY, formatDelta, formatDeltaJPY, formatPlain } = useGridFormat()

  // 実績と見通しの境目。ここまでは売買明細の実績、その先は期待利回りでの見通し。
  // Workers は UTC で動くので、今年・今月は端末の時計で決めて渡す
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1

  const [unit, setUnit] = useState<ViewUnit>(loadUnit)
  const [year, setYear] = useState(currentYear)
  const [overview, setOverview] = useState<LifePlanOverview | null>(null)
  const [startYear, setStartYear] = useState<number | null>(null)
  const [endYear, setEndYear] = useState(() => loadYear(END_YEAR_KEY, currentYear, MAX_END_YEAR, DEFAULT_END_YEAR))
  const [sections, setSections] = useState<MatrixSection[]>([])
  const [showReturns, setShowReturns] = useState(true)
  const [heatmap, setHeatmap] = useState(true)
  // 投資セクションの期間セルを期首からの累積で見るか（既定は期間ごとの額）
  const [cumulativeInvestment, setCumulativeInvestment] = useState(false)
  const [editing, setEditing] = useState<LifePlanSeries | null>(null)
  const [planLoading, setPlanLoading] = useState(true)
  const [matrixLoading, setMatrixLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isMonth = unit === 'month'

  const changeUnit = (next: ViewUnit) => {
    localStorage.setItem(UNIT_KEY, next)
    setUnit(next)
  }

  // ---- ライフプランの元データ ----
  const loadPlan = useCallback(() => {
    if (!accessToken) return
    setPlanLoading(true)
    lifePlanApi.overview(accessToken, currentYear, currentMonth, isMonth ? year : undefined)
      .then((res) => setOverview(res.data))
      .catch(() => {
        setOverview(null)
        setError(t('lp_fetch_error'))
      })
      .finally(() => setPlanLoading(false))
  }, [accessToken, currentYear, currentMonth, isMonth, year, t])

  useEffect(() => { loadPlan() }, [loadPlan])

  // 表の左端はデータがある最初の年から。保存済みの選択があればそれを優先する
  const dataStartYear = Math.min(overview?.dataStartYear ?? currentYear, currentYear)
  useEffect(() => {
    if (overview === null) return
    setStartYear((current) => current ?? loadYear(START_YEAR_KEY, dataStartYear, currentYear, dataStartYear))
  }, [overview, dataStartYear, currentYear])

  const effectiveStartYear = startYear ?? dataStartYear

  // ---- 収支（カテゴリ × 期間）----
  // 年単位は開始年・終了年の入力を打っている途中でも走ってしまうので、少し待ってから取る
  const matrixReady = isMonth || overview !== null
  useEffect(() => {
    if (!accessToken || !matrixReady) return
    setMatrixLoading(true)
    const timer = setTimeout(() => {
      reportsApi.balanceMatrix(
        accessToken,
        isMonth ? year : effectiveStartYear,
        isMonth ? undefined : endYear,
      )
        .then((res) => setSections(res.data.sections as MatrixSection[]))
        .catch(() => {
          setSections([])
          setError(t('matrix_fetch_error'))
        })
        .finally(() => setMatrixLoading(false))
    }, isMonth ? 0 : 300)
    return () => clearTimeout(timer)
  }, [accessToken, matrixReady, isMonth, year, effectiveStartYear, endYear, t])

  const changeStartYear = (next: number) => {
    if (!Number.isInteger(next) || next < dataStartYear || next > currentYear) return
    localStorage.setItem(START_YEAR_KEY, String(next))
    setStartYear(next)
  }

  const changeEndYear = (next: number) => {
    if (!Number.isInteger(next) || next < currentYear || next > MAX_END_YEAR) return
    localStorage.setItem(END_YEAR_KEY, String(next))
    setEndYear(next)
  }

  const labels = useMemo(
    () => ({ noAccountType: t('lp_no_account_type'), noMember: t('lp_no_member') }),
    [t],
  )

  /**
   * 年単位の表。年単位の表示ではそのまま画面に出し、月単位の表示では
   * 「前年 12 月末の残高」と「その年の拠出予定」を月に割り振る土台として使う。
   */
  const annualTable = useMemo(
    () => buildLifePlan({
      buckets: overview?.buckets ?? [],
      members: overview?.members ?? [],
      // 月単位では 1 月の期首（= 前年 12 月末）が要るので、左端を 1 年ぶん手前に置く
      startYear: isMonth ? Math.min(dataStartYear, year - 1) : effectiveStartYear,
      endYear: isMonth ? Math.max(year, currentYear) : endYear,
      pivotYear: currentYear,
      dataStartYear: overview?.dataStartYear ?? null,
      labels,
    }),
    [overview, isMonth, dataStartYear, year, effectiveStartYear, endYear, currentYear, labels],
  )

  const table: LifePlanTable = useMemo(
    () => (isMonth
      ? buildLifePlanMonths({
          annual: annualTable,
          members: overview?.members ?? [],
          year,
          currentYear,
          dataStartYear: overview?.dataStartYear ?? null,
        })
      : annualTable),
    [isMonth, annualTable, overview, year, currentYear],
  )

  const columns = table.columns
  const columnCount = columns.length
  const loading = planLoading || matrixLoading

  /** モードを切り替えた直後は前のモードの列数が残るので、列の数に合わせて読み直す */
  const sectionOf = useCallback(
    (type: SectionType): MatrixSection => {
      const found = sections.find((s) => s.type === type)
      if (!found) return emptySection(type, columnCount)
      const fit = (values: number[]) => Array.from({ length: columnCount }, (_, i) => values[i] ?? 0)
      return {
        ...found,
        months: fit(found.months),
        categories: found.categories.map((cat) => ({ ...cat, months: fit(cat.months) })),
      }
    },
    [sections, columnCount],
  )

  const income = sectionOf('income')
  const expense = sectionOf('expense')
  const transfer = sectionOf('transfer')
  const investment = sectionOf('investment')

  const investmentView = useMemo(
    () => (cumulativeInvestment ? toCumulative(investment) : investment),
    [investment, cumulativeInvestment],
  )

  const net = useMemo(
    () => ({
      months: Array.from({ length: columnCount }, (_, i) => (income.months[i] ?? 0) - (expense.months[i] ?? 0)),
      total: income.total - expense.total,
    }),
    [income, expense, columnCount],
  )

  /** いま表示している期間に「今」が含まれるとき、強調する列の添字（無ければ -1） */
  const currentColumn = isMonth
    ? year === currentYear ? currentMonth - 1 : -1
    : columns.indexOf(currentYear)

  const isMilestone = useCallback(
    (column: number) => !isMonth && columns[column] % 5 === 0,
    [isMonth, columns],
  )

  // 年単位は列が何十本にも伸びて今年が画面外に出るので、読み込み後に今年の列まで寄せる。
  // 月単位は 12 列しかなく、寄せると 1 月が隠れてしまうので触らない
  const scrollerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (isMonth || loading || currentColumn < 0) return
    const el = scrollerRef.current
    if (!el) return
    // 少し手前から見えるようにして、直前の流れも一緒に読めるようにする
    el.scrollLeft = Math.max(0, (currentColumn - 3) * COLUMN_WIDTH)
  }, [isMonth, loading, currentColumn])

  const hasBalance =
    income.categories.length > 0 ||
    expense.categories.length > 0 ||
    transfer.categories.length > 0 ||
    investment.categories.length > 0
  const hasPlan = table.series.length > 0
  const hasData = hasBalance || hasPlan

  /** 列見出し。月単位は「7月」、年単位は西暦 */
  const columnLabel = useCallback(
    (column: number) =>
      isMonth ? t('common_month_n').replace('{n}', String(columns[column])) : String(columns[column]),
    [isMonth, columns, t],
  )

  /**
   * セルが表す条件で絞り込んだ取引一覧への URL。
   * column を省くと表示中の期間まるごと、categoryId を省くとセクション全体（種別のみ）になる。
   */
  const hrefFor = useCallback<HrefFor>(
    ({ types, column, categoryId }) => {
      const params = new URLSearchParams({ type: types })
      if (categoryId) params.set('category', categoryId)
      let from: string
      let to: string
      if (isMonth) {
        if (column === undefined) {
          from = `${year}-01-01`
          to = `${year}-12-31`
        } else {
          const m = columns[column]
          from = `${year}-${pad2(m)}-01`
          to = `${year}-${pad2(m)}-${pad2(lastDayOf(year, m))}`
        }
      } else if (column === undefined) {
        from = `${columns[0] ?? currentYear}-01-01`
        to = `${columns[columnCount - 1] ?? currentYear}-12-31`
      } else {
        from = `${columns[column]}-01-01`
        to = `${columns[column]}-12-31`
      }
      params.set('dateFrom', from)
      params.set('dateTo', to)
      return `/transactions?${params.toString()}`
    },
    [isMonth, year, columns, columnCount, currentYear],
  )

  // ---- ライフプランの行に出す値 ----

  /** 金額のセル。0 と未算出は「—」にして、実績のない期間をひと目で分かるようにする */
  const planCell = (value: number | null) =>
    value === null || Math.round(value) === 0 ? '—' : formatPlain(Math.round(value))

  /** 利回りのセル */
  const rateCell = (rate: number | null) => (rate === null ? '—' : `${rate}%`)

  /** 評価損益のセル。プラスは符号を添えて、含み益か含み損かをひと目で分かるようにする */
  const gainCell = (value: number | null) => {
    const rounded = value === null ? null : Math.round(value)
    if (rounded === null || rounded === 0) return '—'
    return `${rounded > 0 ? '+' : ''}${formatPlain(rounded)}`
  }

  /** 評価損益の色。他の画面と同じく含み益は緑、含み損は赤 */
  const gainTone = (value: number | null) => {
    const rounded = value === null ? null : Math.round(value)
    if (rounded === null || rounded === 0) return undefined
    return rounded > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
  }

  const lastIndex = columnCount - 1
  const finalBalance = table.totalValues[lastIndex] ?? 0
  const planContributionTotal = table.totalContributions.reduce((sum, v) => sum + v, 0)
  /** 合計列に出す期間まるごとの利回り。期首の残高が分からなければ出さない */
  const spanReturnRate = periodReturnPercent(
    table.openingValue,
    table.totalValues[lastIndex] ?? null,
    planContributionTotal,
  )

  /**
   * 合計列（月単位のときだけ）。フロー行は 12 か月の合計、累計行は 12 月末の値を置く。
   * 見出しのホバーでその読み方を添える。
   */
  const showTotal = isMonth
  const planTotalCell = (text: string, tone?: string): GridCell | undefined =>
    showTotal ? { text, tone } : undefined

  const unpricedCount = (overview?.buckets ?? []).reduce((sum, b) => sum + b.unpricedCount, 0)

  /** 制度と拠出枠のまとめ。枠見出しのホバーと編集画面で使う */
  const limitSummary = useCallback((s: LifePlanSeries) => {
    const limit = s.limit
    const parts = [t(SCHEME_LABEL_KEY[limit.scheme])]
    if (limit.annualLimit === null) {
      parts.push(t('lp_limit_annual_none'))
    } else {
      parts.push(
        (limit.annualLimitVaries ? t('lp_limit_annual_varies') : t('lp_limit_annual'))
          .replace('{amount}', formatJPY(limit.annualLimit)),
      )
    }
    // 生涯枠はグループ（NISA 全体）と内枠（成長投資枠）を分けて出す
    if (limit.groupLifetimeLimit !== null && s.lifetimeUsed !== null) {
      parts.push(lifetimeText(limit.groupLifetimeLimit, s.lifetimeUsed))
    }
    if (
      limit.lifetimeLimit !== null &&
      limit.lifetimeLimit !== limit.groupLifetimeLimit &&
      s.schemeLifetimeUsed !== null
    ) {
      parts.push(lifetimeText(limit.lifetimeLimit, s.schemeLifetimeUsed))
    }
    if (limit.eligibleAgeBelow !== null) {
      parts.push(t('lp_limit_age').replace('{age}', String(limit.eligibleAgeBelow)))
    }
    if (limit.closed) parts.push(t('lp_limit_closed'))
    return parts.join(' / ')

    function lifetimeText(total: number, usedAmount: number) {
      return t('lp_limit_lifetime')
        .replace('{limit}', formatJPY(total))
        .replace('{remaining}', formatJPY(Math.max(0, total - usedAmount)))
    }
  }, [t, formatJPY])

  const totalColLabel = t('lp_col_year_total')

  const exportCsv = useCallback(() => {
    const money = (value: number | null) => (value === null ? '' : String(Math.round(value)))
    // 画面の印と同じく、累計かその期間だけかを項目名に添える
    const item = (label: string, period: 'cumulative' | 'period') =>
      `${label}[${t(period === 'cumulative' ? 'lp_period_cumulative' : isMonth ? 'lp_period_month' : 'lp_period_annual')}]`
    const rows: string[][] = []
    const head = [t('lp_col_item')]
    if (showTotal) head.push(totalColLabel)
    rows.push([...head, ...columns.map((_, i) => columnLabel(i))])

    /** 合計列を出すモードでは 2 列目に合計を差し込む */
    const row = (label: string, total: string, cells: string[]) =>
      rows.push(showTotal ? [label, total, ...cells] : [label, ...cells])

    for (const ageRow of table.ageRows) {
      row(ageRow.member.name, '', ageRow.ages.map((age) => (age === null ? '' : String(age))))
    }

    const pushSection = (section: MatrixSection, totalLabel: string) => {
      for (const cat of section.categories) {
        row(
          cat.categoryName || t('matrix_uncategorized'),
          String(signed(section.type, cat.total)),
          cat.months.map((v) => String(signed(section.type, v))),
        )
      }
      row(
        totalLabel,
        String(signed(section.type, section.total)),
        section.months.map((v) => String(signed(section.type, v))),
      )
    }

    pushSection(income, t('matrix_total_income'))
    pushSection(expense, t('matrix_total_expense'))
    row(t('matrix_net'), String(net.total), net.months.map(String))
    row(
      t('matrix_savings_rate'),
      formatRate(savingsRate(net.total, income.total)),
      net.months.map((v, i) => formatRate(savingsRate(v, income.months[i] ?? 0))),
    )

    if (hasPlan) {
      row(item(t('lp_balance'), 'cumulative'), money(table.totalValues[lastIndex] ?? null), table.totalValues.map(money))
      row(item(t('lp_total_principal'), 'cumulative'), money(table.totalPrincipals[lastIndex] ?? null), table.totalPrincipals.map(money))
      row(item(t('lp_total_gain'), 'cumulative'), money(table.totalGains[lastIndex] ?? null), table.totalGains.map(money))
      row(item(t('lp_total_period_gain'), 'period'), '', table.totalPeriodGains.map(money))
      row(
        item(t('lp_total_return'), 'period'),
        spanReturnRate === null ? '' : String(spanReturnRate),
        table.totalReturnRates.map((r) => (r === null ? '' : String(r))),
      )
      row(item(t('lp_total_contribution'), 'period'), String(Math.round(planContributionTotal)), table.totalContributions.map(money))
      for (const s of table.series) {
        row(
          item(t('lp_bucket_contribution').replace('{name}', s.label), 'period'),
          String(Math.round(s.contributions.reduce((sum, v) => sum + v, 0))),
          s.contributions.map(money),
        )
        row(item(t('lp_row_principal'), 'cumulative'), money(s.principals[lastIndex] ?? null), s.principals.map(money))
        row(item(t('lp_row_value'), 'cumulative'), money(s.values[lastIndex] ?? null), s.values.map(money))
        row(item(t('lp_row_gain'), 'cumulative'), money(s.gains[lastIndex] ?? null), s.gains.map(money))
        row(
          item(t('lp_row_return'), 'period'),
          '',
          s.returnRates.map((r) => (r === null ? '' : String(r))),
        )
      }
    }

    if (transfer.categories.length > 0) pushSection(transfer, t('matrix_total_transfer'))
    if (investmentView.categories.length > 0) pushSection(investmentView, t('matrix_total_investment'))

    const csv = rows
      .map((r) => r.map((cell) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(','))
      .join('\r\n')
    // Excel が UTF-8 と判定できるように BOM を付ける
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = isMonth
      ? `zeni-life-plan-${year}.csv`
      : `zeni-life-plan-${effectiveStartYear}-${endYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [
    table, income, expense, transfer, investmentView, net, columns, columnLabel, totalColLabel,
    showTotal, hasPlan, lastIndex, spanReturnRate, planContributionTotal, isMonth, year,
    effectiveStartYear, endYear, t,
  ])

  const prevYear = () => setYear((y) => y - 1)
  const nextYear = () => setYear((y) => y + 1)
  const isAtCurrentYear = year === currentYear

  const tableWidth = ITEM_WIDTH + (showTotal ? TOTAL_WIDTH : 0) + COLUMN_WIDTH * columnCount

  const headCell = 'sticky top-0 z-20 bg-gray-50 px-2 py-1.5 border-b border-gray-200 font-medium text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
  const stickyName = 'sticky left-0 z-10 bg-white dark:bg-gray-900'
  const stickyTotal = 'sticky z-10 bg-gray-50 dark:bg-gray-800'
  const yearInputClass = 'w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'

  const unitTab = (value: ViewUnit, label: string) => (
    <button
      onClick={() => changeUnit(value)}
      data-testid={`life-plan-unit-${value}`}
      className={segItem(unit === value)}
    >
      {label}
    </button>
  )

  /** 行に毎回渡す、列のかたちに関する値 */
  const grid = { unit, showTotal, currentColumn, isMilestone, stickyName, stickyTotal }

  return (
    <div className="h-full flex flex-col min-h-0 gap-4" data-testid="life-plan-page">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div>
          <h1 className={pageTitle}>{t('lp_title')}</h1>
          <p className={pageSubtitle}>{t(isMonth ? 'lp_subtitle_month' : 'lp_subtitle_year')}</p>
        </div>

        <div className={segGroup}>
          {unitTab('month', t('lp_unit_month'))}
          {unitTab('year', t('lp_unit_year'))}
        </div>

        {isMonth ? (
          <div className="flex items-center gap-2 pl-3 border-l border-gray-200 dark:border-gray-700">
            <button onClick={prevYear} data-testid="life-plan-prev-year" className={btnNav}>◀</button>
            <span className="text-base font-semibold text-gray-900 min-w-[5rem] text-center tabular-nums dark:text-gray-100">
              {year}
            </span>
            <button onClick={nextYear} data-testid="life-plan-next-year" className={btnNav}>▶</button>
            {!isAtCurrentYear && (
              <button onClick={() => setYear(currentYear)} className={btnAccentSm}>
                {t('reports_jump_current_year')}
              </button>
            )}
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 pl-3 border-l border-gray-200 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300">
              {t('lp_start_year')}
              <input
                type="number"
                value={effectiveStartYear}
                min={dataStartYear}
                max={currentYear}
                step={1}
                onChange={(e) => changeStartYear(Number(e.target.value))}
                data-testid="life-plan-start-year"
                className={yearInputClass}
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              {t('lp_end_year')}
              <input
                type="number"
                value={endYear}
                min={currentYear}
                max={MAX_END_YEAR}
                step={1}
                onChange={(e) => changeEndYear(Number(e.target.value))}
                data-testid="life-plan-end-year"
                className={yearInputClass}
              />
            </label>
          </>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-3 border-l border-gray-200 text-xs dark:border-gray-700">
          <span className="text-gray-500 dark:text-gray-400">
            {t('matrix_total_income')}{' '}
            <span className="font-semibold text-green-600 tabular-nums dark:text-green-400">{formatJPY(income.total)}</span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t('matrix_total_expense')}{' '}
            <span className="font-semibold text-red-600 tabular-nums dark:text-red-400">{formatJPY(expense.total)}</span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t('matrix_net')}{' '}
            <span
              className={`font-semibold tabular-nums ${deltaTone(net.total) ?? 'text-gray-900 dark:text-gray-100'}`}
              data-testid="life-plan-net-total"
            >
              {formatDeltaJPY(net.total)}
            </span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {t('matrix_savings_rate')}{' '}
            <span className="font-semibold text-gray-900 tabular-nums dark:text-gray-100">
              {formatRate(savingsRate(net.total, income.total))}
            </span>
          </span>
          {hasPlan && (
            <span className="text-gray-500 dark:text-gray-400">
              {t(isMonth ? 'lp_year_end_balance' : 'lp_final_balance')
                .replace('{year}', String(isMonth ? year : endYear))}{' '}
              <span className="font-semibold text-indigo-600 tabular-nums dark:text-indigo-400" data-testid="life-plan-final-balance">
                {formatJPY(Math.round(finalBalance))}
              </span>
            </span>
          )}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
          {hasPlan && (
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap dark:text-gray-300">
              <input
                type="checkbox"
                checked={showReturns}
                onChange={(e) => setShowReturns(e.target.checked)}
                data-testid="life-plan-show-returns"
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
              />
              {t('lp_show_returns')}
            </label>
          )}
          {investment.categories.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap dark:text-gray-300">
              <input
                type="checkbox"
                checked={cumulativeInvestment}
                onChange={(e) => setCumulativeInvestment(e.target.checked)}
                data-testid="life-plan-cumulative-toggle"
                className="rounded border-gray-300 text-violet-600 focus:ring-violet-500 dark:border-gray-600 dark:bg-gray-800"
              />
              {t('matrix_cumulative_investment')}
            </label>
          )}
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap dark:text-gray-300">
            <input
              type="checkbox"
              checked={heatmap}
              onChange={(e) => setHeatmap(e.target.checked)}
              data-testid="life-plan-heatmap-toggle"
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
            />
            {t('matrix_heatmap')}
          </label>
          <button
            onClick={exportCsv}
            disabled={!hasData}
            data-testid="life-plan-export-csv"
            className={btnSecondarySm}
          >
            {t('lp_export_csv')}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {unpricedCount > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-300">
          {t('lp_unpriced_note').replace('{count}', String(unpricedCount))}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
          {t('common_loading')}
        </div>
      ) : !hasData ? (
        <div className="flex-1 flex items-center justify-center rounded-xl border border-gray-200 bg-white px-6 text-center text-sm text-gray-400 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500">
          {t('lp_empty')}
        </div>
      ) : (
        <>
          <div
            ref={scrollerRef}
            className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
          >
            <table
              className="table-fixed border-separate border-spacing-0 text-[11px]"
              style={{ width: tableWidth }}
            >
              <colgroup>
                <col style={{ width: ITEM_WIDTH }} />
                {showTotal && <col style={{ width: TOTAL_WIDTH }} />}
                {columns.map((column) => (
                  <col key={column} style={{ width: COLUMN_WIDTH }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{ height: HEADER_HEIGHT, left: 0 }}
                    className={`${headCell} sticky left-0 z-30 text-left ${showTotal ? '' : 'border-r border-gray-300 dark:border-gray-600'}`}
                  >
                    {t('lp_col_item')}
                  </th>
                  {showTotal && (
                    <th
                      style={{ height: HEADER_HEIGHT, left: ITEM_WIDTH }}
                      title={t('lp_col_year_total_hint')}
                      className={`${headCell} sticky z-30 text-right border-r border-gray-300 dark:border-gray-600`}
                    >
                      {totalColLabel}
                    </th>
                  )}
                  {columns.map((column, i) => (
                    <th
                      key={column}
                      style={{ height: HEADER_HEIGHT }}
                      data-testid={`life-plan-column-${column}`}
                      className={`${headCell} text-right tabular-nums ${
                        i === currentColumn
                          ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-950'
                          : isMilestone(i)
                          ? 'text-indigo-500 dark:text-indigo-400'
                          : ''
                      }`}
                    >
                      {columnLabel(i)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* 年齢は表のいちばん上。何歳のときの数字なのかを先に読ませる */}
                {table.ageRows.map((row) => (
                  <Row
                    key={row.member.id}
                    label={row.member.name}
                    testId="life-plan-row-age"
                    {...grid}
                    cells={row.ages.map((age) => ({
                      text: age === null ? '—' : t('member_age_display').replace('{age}', String(age)),
                    }))}
                  />
                ))}

                <SectionRows
                  section={income}
                  hrefFor={hrefFor}
                  totalLabel={t('matrix_total_income')}
                  heatmap={heatmap}
                  {...grid}
                />
                <SectionRows
                  section={expense}
                  hrefFor={hrefFor}
                  totalLabel={t('matrix_total_expense')}
                  heatmap={heatmap}
                  {...grid}
                />

                <Row
                  label={t('matrix_net')}
                  testId="life-plan-row-net"
                  {...grid}
                  emphasis="total"
                  linkTitle={t('matrix_open_transactions')}
                  totalCell={{
                    text: formatDeltaJPY(net.total),
                    tone: deltaTone(net.total),
                    href: hrefFor({ types: 'income,expense' }),
                  }}
                  cells={net.months.map((v, i) => ({
                    text: v === 0 ? '—' : formatDelta(v),
                    tone: deltaTone(v),
                    href:
                      (income.months[i] ?? 0) === 0 && (expense.months[i] ?? 0) === 0
                        ? undefined
                        : hrefFor({ types: 'income,expense', column: i }),
                  }))}
                />
                <Row
                  label={t('matrix_savings_rate')}
                  testId="life-plan-row-savings-rate"
                  {...grid}
                  emphasis="total"
                  totalCell={{ text: formatRate(savingsRate(net.total, income.total)) }}
                  cells={net.months.map((v, i) => {
                    const rate = savingsRate(v, income.months[i] ?? 0)
                    return {
                      text: formatRate(rate),
                      tone: rate === null || rate >= 0 ? undefined : 'text-red-600 dark:text-red-400',
                    }
                  })}
                />

                {/* ここから下がライフプラン。収支で残った分がどこまで積み上がるかを続けて読む */}
                {hasPlan && (
                  <>
                    <Row
                      label={t('lp_balance')}
                      testId="life-plan-row-balance"
                      period="cumulative"
                      {...grid}
                      emphasis="strong"
                      totalCell={planTotalCell(planCell(table.totalValues[lastIndex] ?? null))}
                      cells={table.totalValues.map((v) => ({ text: planCell(v) }))}
                    />
                    <Row
                      label={t('lp_total_principal')}
                      testId="life-plan-row-total-principal"
                      period="cumulative"
                      titleText={t('lp_total_principal_hint')}
                      {...grid}
                      totalCell={planTotalCell(planCell(table.totalPrincipals[lastIndex] ?? null))}
                      cells={table.totalPrincipals.map((v) => ({ text: planCell(v) }))}
                    />
                    <Row
                      label={t('lp_total_gain')}
                      testId="life-plan-row-total-gain"
                      period="cumulative"
                      titleText={t('lp_total_gain_hint')}
                      {...grid}
                      totalCell={planTotalCell(
                        gainCell(table.totalGains[lastIndex] ?? null),
                        gainTone(table.totalGains[lastIndex] ?? null),
                      )}
                      cells={table.totalGains.map((v) => ({ text: gainCell(v), tone: gainTone(v) }))}
                    />
                    <Row
                      label={t('lp_total_period_gain')}
                      testId="life-plan-row-total-period-gain"
                      period="period"
                      titleText={t('lp_total_period_gain_hint')}
                      {...grid}
                      totalCell={planTotalCell('—')}
                      cells={table.totalPeriodGains.map((v) => ({ text: gainCell(v), tone: gainTone(v) }))}
                    />
                    {showReturns && (
                      <Row
                        label={t('lp_total_return')}
                        testId="life-plan-row-total-return"
                        period="period"
                        {...grid}
                        totalCell={planTotalCell(rateCell(spanReturnRate))}
                        cells={table.totalReturnRates.map((r) => ({ text: rateCell(r) }))}
                      />
                    )}
                    <Row
                      label={t('lp_total_contribution')}
                      testId="life-plan-row-total-contribution"
                      period="period"
                      {...grid}
                      emphasis="total"
                      totalCell={planTotalCell(planCell(planContributionTotal))}
                      cells={table.totalContributions.map((v) => ({ text: planCell(v) }))}
                    />

                    {table.series.map((s) => (
                      <BucketRows
                        key={`${s.bucket.memberKey}-${s.bucket.accountType}`}
                        series={s}
                        showReturns={showReturns}
                        {...grid}
                        planCell={planCell}
                        rateCell={rateCell}
                        gainCell={gainCell}
                        gainTone={gainTone}
                        limitSummary={limitSummary}
                        onEdit={() => setEditing(s)}
                      />
                    ))}
                  </>
                )}

                {transfer.categories.length > 0 && (
                  <SectionRows
                    section={transfer}
                    hrefFor={hrefFor}
                    totalLabel={t('matrix_total_transfer')}
                    heatmap={heatmap}
                    {...grid}
                  />
                )}

                {investmentView.categories.length > 0 && (
                  <SectionRows
                    section={investmentView}
                    hrefFor={hrefFor}
                    totalLabel={t('matrix_total_investment')}
                    heatmap={heatmap}
                    {...grid}
                  />
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {investment.categories.length > 0 ? t('matrix_transfer_investment_note') : t('matrix_transfer_note')}
            {cumulativeInvestment && investment.categories.length > 0 && ` ${t('matrix_cumulative_investment_note')}`}
          </p>
          {hasPlan && (
            <>
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {t(isMonth ? 'lp_period_legend_month' : 'lp_period_legend')}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                {t('lp_note').replaceAll('{year}', String(currentYear))}
                {isMonth && ` ${t('lp_month_forecast_note')}`}
              </p>
            </>
          )}
        </>
      )}

      {editing && accessToken && (
        <AssumptionModal
          series={editing}
          token={accessToken}
          pivotYear={currentYear}
          labels={labels}
          limitSummary={limitSummary(editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadPlan() }}
        />
      )}
    </div>
  )
}

/**
 * その行の数字が「期末までの積み上げ」なのか「その期間だけの額」なのかを示す印。
 * 残高と拠出が同じ表に並ぶので、行ごとにどちらなのかを目印で分かるようにする。
 */
function PeriodBadge({ period, unit }: { period: 'cumulative' | 'period'; unit: ViewUnit }) {
  const { t } = useLanguage()
  const cumulative = period === 'cumulative'
  const label = cumulative
    ? t('lp_period_cumulative')
    : t(unit === 'month' ? 'lp_period_month' : 'lp_period_annual')
  const hint = cumulative
    ? t(unit === 'month' ? 'lp_period_cumulative_hint_month' : 'lp_period_cumulative_hint')
    : t(unit === 'month' ? 'lp_period_month_hint' : 'lp_period_annual_hint')
  return (
    <span
      title={hint}
      data-testid={`life-plan-period-${period}`}
      className={`shrink-0 rounded border px-1 text-[10px] font-normal leading-normal ${
        cumulative
          ? 'border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500'
          : 'border-sky-200 text-sky-600 dark:border-sky-900 dark:text-sky-400'
      }`}
    >
      {label}
    </span>
  )
}

/** 1 行分のセル。列は右寄せ・等幅で、今の列と 5 年ごとの節目を強調する */
function Row({
  unit,
  label,
  testId,
  cells,
  totalCell,
  showTotal,
  currentColumn,
  isMilestone,
  stickyName,
  stickyTotal,
  emphasis,
  labelExtra,
  titleText,
  period,
  linkTitle,
}: GridRowProps & { stickyName: string; stickyTotal: string }) {
  const { t } = useLanguage()
  const base = emphasis
    ? 'py-1.5 border-b border-gray-200 border-t-2 border-t-gray-300 font-semibold dark:border-gray-700 dark:border-t-gray-600'
    : 'py-1 border-b border-gray-100 dark:border-gray-800'
  const defaultTone =
    emphasis === 'strong'
      ? 'text-indigo-600 dark:text-indigo-400'
      : emphasis === 'total'
      ? 'text-gray-700 dark:text-gray-200'
      : 'text-gray-600 dark:text-gray-300'
  const muted = 'text-gray-300 dark:text-gray-600'

  return (
    <tr data-testid={testId}>
      <td
        style={{ left: 0 }}
        className={`${base} ${stickyName} px-2 ${showTotal ? '' : 'border-r border-gray-300 dark:border-r-gray-600'} ${
          emphasis === 'strong' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'
        }`}
      >
        <CellLink href={totalCell?.href} title={linkTitle}>
          <span className="flex items-center gap-1 min-w-0">
            <span className="truncate" title={titleText ?? label}>{label}</span>
            {period && <PeriodBadge period={period} unit={unit} />}
            {labelExtra}
          </span>
        </CellLink>
      </td>
      {showTotal && (
        <td
          style={{ left: ITEM_WIDTH }}
          className={`${base} ${stickyTotal} px-2 text-right tabular-nums border-r border-gray-300 dark:border-r-gray-600 ${
            totalCell === undefined || totalCell.text === '—' ? muted : totalCell.tone ?? defaultTone
          }`}
        >
          <CellLink href={totalCell?.href} title={linkTitle}>{totalCell?.text ?? ''}</CellLink>
        </td>
      )}
      {cells.map((cell, i) => (
        <td
          key={i}
          style={cell.heat ? { backgroundColor: cell.heat } : undefined}
          className={`${base} px-2 text-right tabular-nums whitespace-nowrap ${
            cell.text === '—' ? muted : cell.tone ?? defaultTone
          } ${
            cell.heat
              ? ''
              : i === currentColumn
              ? 'bg-indigo-50/60 dark:bg-indigo-950/40'
              : isMilestone(i)
              ? 'bg-gray-50/60 dark:bg-gray-800/40'
              : ''
          }`}
        >
          <CellLink href={cell.href} title={cell.href && (linkTitle ?? t('matrix_open_transactions'))}>
            {cell.text}
          </CellLink>
        </td>
      ))}
    </tr>
  )
}

/** 1 セクション（収入/支出/振替/投資）のカテゴリ行と合計行 */
function SectionRows({
  section,
  hrefFor,
  totalLabel,
  heatmap,
  unit,
  showTotal,
  currentColumn,
  isMilestone,
  stickyName,
  stickyTotal,
}: {
  section: MatrixSection
  hrefFor: HrefFor
  totalLabel: string
  heatmap: boolean
  unit: ViewUnit
  showTotal: boolean
  currentColumn: number
  isMilestone: (column: number) => boolean
  stickyName: string
  stickyTotal: string
}) {
  const { t } = useLanguage()
  const { formatSignedJPY, formatSigned } = useGridFormat()
  const tone = AMOUNT_TONE[section.type]
  // 投資は取引ではなく証券口座の売買明細が元なので、取引一覧では絞り込めない
  const drillType = section.type === 'investment' ? null : section.type
  const drillTitle = t('matrix_open_transactions')
  const grid = { unit, showTotal, currentColumn, isMilestone, stickyName, stickyTotal }

  return (
    <>
      {section.categories.map((cat) => {
        const max = Math.max(...cat.months.map(Math.abs))
        const categoryHref = drillType
          ? hrefFor({ types: drillType, categoryId: cat.categoryId })
          : undefined
        return (
          <Row
            key={`${section.type}-${cat.categoryId}`}
            label={`${cat.categoryIcon ? `${cat.categoryIcon} ` : ''}${cat.categoryName || t('matrix_uncategorized')}`}
            testId={`life-plan-row-${section.type}`}
            {...grid}
            linkTitle={drillTitle}
            labelExtra={
              <span
                className="w-2 h-2 rounded-full shrink-0 order-first"
                style={{ backgroundColor: cat.categoryColor ?? '#9ca3af' }}
              />
            }
            totalCell={{
              text: formatSignedJPY(signed(section.type, cat.total)),
              tone: `font-semibold ${tone}`,
              href: categoryHref,
            }}
            cells={cat.months.map((amount, i) => ({
              text: amount === 0 ? '—' : formatSigned(signed(section.type, amount)),
              tone: amount === 0 ? undefined : tone,
              href: drillType && amount !== 0
                ? hrefFor({ types: drillType, column: i, categoryId: cat.categoryId })
                : undefined,
              heat: heatmap && amount !== 0 && max > 0
                ? `rgba(${HEAT_RGB[section.type]}, ${(0.06 + 0.26 * (Math.abs(amount) / max)).toFixed(3)})`
                : undefined,
            }))}
          />
        )
      })}
      <Row
        label={totalLabel}
        testId={`life-plan-total-${section.type}`}
        {...grid}
        emphasis="total"
        linkTitle={drillTitle}
        totalCell={{
          text: formatSignedJPY(signed(section.type, section.total)),
          tone,
          href: drillType && section.total !== 0 ? hrefFor({ types: drillType }) : undefined,
        }}
        cells={section.months.map((v, i) => ({
          text: v === 0 ? '—' : formatSigned(signed(section.type, v)),
          tone: v === 0 ? undefined : tone,
          href: drillType && v !== 0 ? hrefFor({ types: drillType, column: i }) : undefined,
        }))}
      />
    </>
  )
}

/** 1 枠分の行（その期間の拠出額 / 元本累計 / 評価額 / 評価損益 / 利回り） */
function BucketRows({
  series,
  showReturns,
  unit,
  showTotal,
  currentColumn,
  isMilestone,
  stickyName,
  stickyTotal,
  planCell,
  rateCell,
  gainCell,
  gainTone,
  limitSummary,
  onEdit,
}: {
  series: LifePlanSeries
  showReturns: boolean
  unit: ViewUnit
  showTotal: boolean
  currentColumn: number
  isMilestone: (column: number) => boolean
  stickyName: string
  stickyTotal: string
  planCell: (value: number | null) => string
  rateCell: (rate: number | null) => string
  gainCell: (value: number | null) => string
  gainTone: (value: number | null) => string | undefined
  limitSummary: (series: LifePlanSeries) => string
  onEdit: () => void
}) {
  const { t } = useLanguage()
  const grid = { unit, showTotal, currentColumn, isMilestone, stickyName, stickyTotal }
  const last = series.contributions.length - 1
  /** 合計列はフロー行なら期間の合計、累計行なら最後の列の値 */
  const totalOf = (text: string, tone?: string) => (showTotal ? { text, tone } : undefined)
  const contributionTotal = series.contributions.reduce((sum, v) => sum + v, 0)

  return (
    <>
      <Row
        label={t('lp_bucket_contribution').replace('{name}', series.label)}
        titleText={`${t('lp_bucket_contribution').replace('{name}', series.label)}\n${limitSummary(series)}`}
        testId="life-plan-row-bucket"
        period="period"
        {...grid}
        emphasis="total"
        totalCell={totalOf(planCell(contributionTotal))}
        cells={series.contributions.map((v) => ({ text: planCell(v) }))}
        labelExtra={
          <>
            {series.lifetimeFilledYear !== null && (
              <span
                title={t('lp_limit_filled').replace('{year}', String(series.lifetimeFilledYear))}
                data-testid="life-plan-limit-filled"
                className="shrink-0 text-[10px] text-amber-600 dark:text-amber-400"
              >
                🔒{series.lifetimeFilledYear}
              </span>
            )}
            <button
              onClick={onEdit}
              title={t('lp_edit_assumption')}
              aria-label={t('lp_edit_assumption')}
              data-testid="life-plan-edit-assumption"
              className={btnIconAccent}
            >
              ✏️
            </button>
          </>
        }
      />
      <Row
        label={t('lp_row_principal')}
        testId="life-plan-row-principal"
        period="cumulative"
        {...grid}
        totalCell={totalOf(planCell(series.principals[last] ?? null))}
        cells={series.principals.map((v) => ({ text: planCell(v) }))}
      />
      <Row
        label={t('lp_row_value')}
        testId="life-plan-row-value"
        period="cumulative"
        {...grid}
        totalCell={totalOf(planCell(series.values[last] ?? null))}
        cells={series.values.map((v) => ({ text: planCell(v) }))}
      />
      <Row
        label={t('lp_row_gain')}
        testId="life-plan-row-gain"
        period="cumulative"
        {...grid}
        totalCell={totalOf(gainCell(series.gains[last] ?? null), gainTone(series.gains[last] ?? null))}
        cells={series.gains.map((v) => ({ text: gainCell(v), tone: gainTone(v) }))}
      />
      {showReturns && (
        <Row
          label={t('lp_row_return')}
          testId="life-plan-row-return"
          period="period"
          {...grid}
          totalCell={totalOf('—')}
          cells={series.returnRates.map((r) => ({ text: rateCell(r) }))}
        />
      )}
    </>
  )
}

/**
 * 枠の前提（年間拠出額・期待利回り・拠出をやめる日）の編集。
 * 枠そのものは保有商品から導出されるので、ここで編集するのは将来の前提だけ。
 */
function AssumptionModal({
  series,
  token,
  pivotYear,
  labels,
  limitSummary,
  onClose,
  onSaved,
}: {
  series: LifePlanSeries
  token: string
  pivotYear: number
  labels: { noAccountType: string; noMember: string }
  limitSummary: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const bucket = series.bucket
  const limit: ContributionLimit = series.limit
  const [annualAmount, setAnnualAmount] = useState(String(bucket.annualAmount))
  const [returnRate, setReturnRate] = useState(bucket.annualReturnRate === null ? '' : String(bucket.annualReturnRate))
  const [endDate, setEndDate] = useState(bucket.contributionEndDate ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** 年間投資枠を超えていれば、その旨のメッセージ。収まっていれば null */
  const annualLimitError = (() => {
    const amount = Number(annualAmount)
    if (!Number.isFinite(amount)) return null
    if (limit.closed && amount > 0) return t('lp_limit_closed')
    if (limit.annualLimit === null || limit.annualLimitVaries) return null
    if (amount <= limit.annualLimit) return null
    return t('lp_limit_error_annual').replace('{amount}', formatJPY(limit.annualLimit))
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // 制度上入れられない額は保存させない（表の計算でも枠に丸めるが、
    // 入れた値と表の数字が食い違うと混乱するので入口で止める）
    if (annualLimitError !== null) {
      setError(annualLimitError)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await lifePlanApi.saveSetting(token, {
        memberKey: bucket.memberKey,
        accountType: bucket.accountType,
        annualAmount: Number(annualAmount) || 0,
        // 空欄は未設定。0% として見通しを引く
        annualReturnRate: returnRate.trim() === '' ? null : Number(returnRate),
        contributionEndDate: endDate === '' ? null : endDate,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const inputClass = 'border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
  const actual = bucket.actualReturnRate

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900">
        <h2 className={`${modalTitle} mb-1`}>
          {t('lp_modal_title').replace('{name}', bucketLabel(bucket, labels))}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('lp_pivot').replace('{year}', String(pivotYear))}:{' '}
          {t('lp_pivot_detail')
            .replace('{principal}', formatJPY(Math.round(bucket.currentPrincipal)))
            .replace('{value}', formatJPY(Math.round(bucket.currentValue)))}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="life-plan-limit-summary">
          {limitSummary}
        </p>
        <p className="text-xs mb-4 text-gray-500 dark:text-gray-400" data-testid="life-plan-actual-return">
          {actual === null
            ? t('lp_actual_return_none')
            : t('lp_actual_return').replace('{rate}', String(actual))}
        </p>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('lp_annual_amount')}
            </label>
            <input
              type="number"
              step="any"
              min="0"
              max={limit.closed ? 0 : limit.annualLimitVaries ? undefined : limit.annualLimit ?? undefined}
              value={annualAmount}
              onChange={(e) => setAnnualAmount(e.target.value)}
              data-testid="life-plan-annual-amount"
              className={inputClass}
            />
            {annualLimitError !== null && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="life-plan-annual-amount-error">
                {annualLimitError}
              </p>
            )}
            {limit.lifetimeGroup === 'nisa' && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('lp_limit_group_note')}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('lp_return_rate')}
            </label>
            <input
              type="number"
              step="any"
              value={returnRate}
              placeholder="6"
              onChange={(e) => setReturnRate(e.target.value)}
              data-testid="life-plan-return-rate"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('lp_return_rate_hint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('lp_contribution_end_date')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="life-plan-contribution-end-date"
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('lp_contribution_end_date_hint')}</p>
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={btnGhost}>
              {t('common_cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="life-plan-save-assumption"
              className={btnPrimary}
            >
              {t(saving ? 'common_saving' : 'common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
